import React, { useState, useEffect, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Dialog,
  Stack,
  Slide,
  Typography,
  Divider,
  Link,
  Alert,
  AlertTitle,
} from '@mui/material';
import CheckoutMap from '@/templates/plus/sections/CheckoutMap';
import useEventContext from '@/contexts/event/useEventContext';
import EventSummary from './EventNav/EventSummary';
import EventInfo from '@/templates/plus/sections/EventNav/EventInfo';
import HeaderMobile from '@/templates/plus/sections/HeaderMobile';
import TicketsSelector from '@/templates/plus/sections/tickets/TicketsSelector';
import TicketSummary from '@/templates/plus/sections/tickets/TicketSummary';
import { styled } from '@mui/material/styles';
import { useTicketsSelected } from '@/store';
import FooterSummary from '@/templates/plus/sections/FooterSummary';
import Iconify from '@/components/Iconify';
import BookingSummary from '@/templates/plus/sections/EventNav/BookingSummary';
import BackButton from '@/templates/plus/components/BackButton';
import PromotionButton from '../../../sections/promotion/PromotionButton';
import ArrowIcon from '@/components/ArrowIcon';
import useAnalyticsEvents from '@/hooks/useAnalyticsEvents';
import useEventStatus from '@/contexts/event/useEventStatus';
import EventEmpty from '@/assets/illustration_EventEmpty';

const HOME_OPEN = 10;
const HOME_CLOSE = 50;
const HOME_CLOSE_EMPTY = 70;

const BoxStyle = styled(Box)(({ theme }) => ({
  transition: theme.transitions.create(['height', 'top'], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.shorter,
  }),
}));

const ExpandMore = styled((props) => {
  const { expand, ...other } = props;
  return (
    <Box
      sx={{
        'width': '32px',
        'height': '32px',
        '& .MuiSvgIcon-root': {
          fill: 'none',
          fontSize: '32px',
          width: '32px',
          height: '32px',
        },
      }}
      {...other}
    >
      <ArrowIcon />
    </Box>
  );
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function EventCartDesktop() {
  const { t } = useTranslation();
  const [view, setView] = useState('home');
  const [show, setShow] = useState(false);
  const [sizeClose, setSizeClose] = useState(HOME_CLOSE);
  const { hasTickets } = useTicketsSelected();
  const { showEventDetails } = useAnalyticsEvents();
  const { eventStatus } = useEventStatus();

  const [openEventInfo, setOpenEventInfo] = useState(false);
  const toggleOpenEventInfo = () => {
    setOpenEventInfo((prev) => {
      if (!prev) {
        showEventDetails();
      }
      return !prev;
    });
  };

  const [open, setOpen] = useState(false);
  const toggleDrawer = () => {
    setOpen((prev) => !prev);
  };

  const {
    event: {
      event: { use_seats, about },
    },
  } = useEventContext();

  const onTicketCategorySelected = () => {
    // toggleDrawer();
    setView('summary');
  };

  const onBack = () => {
    setView('home');
  };

  const getDrawPosition = () => {
    if (view === 'summary') {
      return {
        top: open ? `${HOME_OPEN}%` : `${sizeClose}%`,
        height: open ? `${100 - HOME_OPEN}%` : `${100 - sizeClose}%`,
      };
    }
    return {
      top: open ? `${HOME_OPEN}%` : `${sizeClose}%`,
      height: open ? `${100 - HOME_OPEN}%` : `${100 - sizeClose}%`,
    };
  };

  useEffect(() => {
    setShow(true);
  }, []);

  useEffect(() => {
    if (eventStatus === 'inactive' || eventStatus === 'expired') {
      setSizeClose(HOME_CLOSE_EMPTY);
    }
  }, [eventStatus]);

  return (
    <>
      {view === 'home' && <HeaderMobile onInfo={toggleOpenEventInfo} />}
      {view === 'summary' && (
        <HeaderMobile onInfo={toggleOpenEventInfo}>
          <Container>
            <Stack direction="row" spacing={0} sx={{ alignItems: 'center' }}>
              <BackButton
                onClick={onBack}
                sx={{
                  position: 'unset',
                }}
              />

              <Box sx={{ flexGrow: 1 }}></Box>
              <Box
                sx={{
                  overflow: 'hidden',
                }}
              >
                <PromotionButton rounded />
              </Box>
            </Stack>
          </Container>
        </HeaderMobile>
      )}
      <Dialog fullScreen open={openEventInfo} TransitionComponent={Transition}>
        <Container>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: '56px',
            }}
          >
            <Typography
              variant="subtitle2"
              component="div"
              sx={{
                fontWeight: 600,
                fontSize: '12px',
              }}
            >
              {t('checkout.global.aboutEvent')}
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Box
              sx={{
                'width': '20px',
                'height': '20px',
                'display': 'flex',
                'alignItems': 'center',
                'justifyContent': 'center',
                '&:hover': {
                  cursor: 'pointer',
                },
              }}
              onClick={toggleOpenEventInfo}
            >
              <Iconify
                width={16}
                height={16}
                icon={'material-symbols:close'}
                sx={{
                  color: (theme) =>
                    theme.palette.mode === 'light'
                      ? theme.palette.grey[800]
                      : theme.palette.grey[200],
                }}
              />
            </Box>
          </Box>
        </Container>
        <Divider sx={{ mb: 2 }} />
        <Container>
          <EventInfo />
        </Container>
      </Dialog>
      <Box
        sx={{
          pt: 8,
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          position: 'relative',
          background:
            eventStatus !== 'active' && use_seats ? '#FAFAFF' : '#FFF',
        }}
      >
        {show && (
          <>
            <Container
              sx={{
                height: `calc(${sizeClose}% - 38px)`,
                overflow: 'scroll',
              }}
            >
              <EventSummary />
              <Box
                sx={{
                  mt: 2,
                  pt: 1,
                  borderRadius: '16px',
                  width: '100%',
                  minHeight: '60px',
                  backgroundColor: (theme) => theme.palette.primary.palette[50],
                }}
              >
                <Container>
                  <Typography variant="subtitle2" component="div">
                    {t('checkout.global.aboutEvent')}
                  </Typography>
                  <Typography
                    variant="caption2"
                    component="div"
                    sx={{ color: (theme) => theme.palette.grey[500] }}
                  >
                    {about.replace(/<[^>]+>/g, '').substring(0, 80)}
                  </Typography>
                  <Link
                    variant="caption"
                    sx={{
                      'textDecoration': 'underline',
                      '&:hover': {
                        cursor: 'pointer',
                      },
                      'color': (theme) => theme.palette.secondary.main,
                    }}
                    onClick={toggleOpenEventInfo}
                  >
                    {t('checkout.global.more')}
                  </Link>
                </Container>
              </Box>
            </Container>
          </>
        )}
        <BoxStyle
          sx={{
            ...getDrawPosition(),
            position: 'absolute',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            visibility: 'visible',
            right: 0,
            left: 0,
            background: '#FFF',
            width: '100%',
            overflow: 'scroll',
            boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.16)',
          }}
        >
          {eventStatus === 'active' && (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
              onClick={toggleDrawer}
            >
              <ExpandMore expand={open} />
            </Box>
          )}

          <Container sx={{ pt: 0 }}>
            {show && (
              <>
                <Slide
                  direction="left"
                  in={view === 'summary'}
                  mountOnEnter
                  unmountOnExit
                >
                  <Box
                    sx={{
                      position: 'relative',
                      width: '100%',
                      height: '100%',
                      overflow: 'scroll',
                    }}
                  >
                    <TicketSummary hidePromo />
                    <Divider />
                    <BookingSummary />
                    {hasTickets && (
                      <Box
                        sx={{
                          height: '77px',
                          width: '100%',
                        }}
                      ></Box>
                    )}
                  </Box>
                </Slide>
                {view === 'home' && (
                  <Box
                    sx={{
                      position: 'relative',
                      width: '100%',
                      height: '100%',
                      overflow: 'scroll',
                    }}
                  >
                    {eventStatus === 'active' && (
                      <CheckoutMap
                        disabledAlerts
                        disabledSaturated
                        onHold={() => {}}
                        sx={{
                          height: `calc(${sizeClose}% - 38px)`,
                        }}
                      />
                    )}
                    {eventStatus === 'inactive' && (
                      <Box
                        sx={{
                          mt: '24px',
                        }}
                      >
                        <Alert
                          sx={{
                            '& .MuiAlert-icon': {
                              fontSize: 40,
                              color: '#1B8FFF',
                            },
                          }}
                          severity="info"
                        >
                          <AlertTitle>
                            {t('checkout.home.alerts.noTickets')}
                          </AlertTitle>
                          {t('checkout.home.alerts.noTicketsMsg')}
                        </Alert>
                      </Box>
                    )}
                    {eventStatus === 'expired' && (
                      <Box
                        sx={{
                          mt: '24px',
                        }}
                      >
                        <Alert
                          sx={{
                            '& .MuiAlert-icon': {
                              fontSize: 40,
                              color: '#1B8FFF',
                            },
                            'background': '#C8CCF2',
                          }}
                          severity="info"
                        >
                          <AlertTitle>
                            {t('checkout.home.alerts.expired')}
                          </AlertTitle>
                          {t('checkout.home.alerts.expiredMsg')}{' '}
                          <Link
                            variant="caption"
                            underline="always"
                            href="https://www.boletia.com"
                          >
                            boletia.com
                          </Link>
                        </Alert>
                      </Box>
                    )}
                  </Box>
                )}
              </>
            )}
          </Container>
          {hasTickets && (
            <Box
              sx={{
                height: '77px',
                width: '100%',
              }}
            ></Box>
          )}
        </BoxStyle>
      </Box>
      {show && hasTickets && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            background: '#FFF',
            boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.16)',
          }}
        >
          <Container>
            <FooterSummary />
          </Container>
        </Box>
      )}
    </>
  );
}
